import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import type { PromoDrillParams } from '@/features/promotion/types/promotion.types';
import { downloadBlob } from '@/utils/download';
import { DEFAULT_MP_PAGE_SIZE } from '../constants/marketPromotion.constants';
import {
  mpAdjustmentResponseSchema,
  mpApprovalResponseSchema,
  mpRegionListSchema,
  mpSummaryResponseSchema,
  type DecideDonationPayload,
  type MpAdjustmentResponse,
  type MpAdjustmentRow,
  type MpApprovalResponse,
  type MpApprovalSearch,
  type MpRegionRow,
  type MpRegionSearch,
  type MpReportSearch,
  type MpSummaryResponse,
  type VerifyMisPayload,
} from '../types/marketPromotion.types';

export const marketPromotionQueryKeys = {
  all: () => ['market-promotion'] as const,
  adjustment: (params: MpReportSearch) =>
    [...marketPromotionQueryKeys.all(), 'adjustment', params] as const,
  distributedItem: (promoItemId: number, params: PromoDrillParams) =>
    [...marketPromotionQueryKeys.all(), 'distributed-item', params, promoItemId] as const,
  summary: (params: MpReportSearch) =>
    [...marketPromotionQueryKeys.all(), 'summary', params] as const,
  regions: (params: MpRegionSearch) =>
    [...marketPromotionQueryKeys.all(), 'regions', params] as const,
  approvals: (params: MpApprovalSearch, mis: boolean) =>
    [...marketPromotionQueryKeys.all(), 'approvals', mis, params] as const,
};

export const mpAdjustmentInfiniteQueryOptions = (params: MpReportSearch) =>
  infiniteQueryOptions({
    queryKey: marketPromotionQueryKeys.adjustment(params),
    queryFn: async ({ pageParam }): Promise<MpAdjustmentResponse> => {
      const res = await apiClient.get(API.marketPromotion.adjustment, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return mpAdjustmentResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const distributedMpItemQueryOptions = (promoItemId: number, params: PromoDrillParams) =>
  queryOptions({
    queryKey: marketPromotionQueryKeys.distributedItem(promoItemId, params),
    queryFn: async (): Promise<MpAdjustmentRow | null> => {
      const res = await apiClient.get(API.marketPromotion.adjustment, {
        params: { ...params, promoItemId, page: FIRST_PAGE, pageSize: 1 },
      });
      return mpAdjustmentResponseSchema.parse(res.data).data[0] ?? null;
    },
    staleTime: STALE_TIME,
  });

export const mpSummaryInfiniteQueryOptions = (params: MpReportSearch) =>
  infiniteQueryOptions({
    queryKey: marketPromotionQueryKeys.summary(params),
    queryFn: async ({ pageParam }): Promise<MpSummaryResponse> => {
      const res = await apiClient.get(API.marketPromotion.distributionSummary, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return mpSummaryResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const mpRegionsQueryOptions = (params: MpRegionSearch) =>
  queryOptions({
    queryKey: marketPromotionQueryKeys.regions(params),
    queryFn: async (): Promise<MpRegionRow[]> => {
      const res = await apiClient.get(API.marketPromotion.regionSummary, { params });
      return mpRegionListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const mpApprovalsInfiniteQueryOptions = (params: MpApprovalSearch, mis: boolean) =>
  infiniteQueryOptions({
    queryKey: marketPromotionQueryKeys.approvals(params, mis),
    queryFn: async ({ pageParam }): Promise<MpApprovalResponse> => {
      const res = await apiClient.get(
        mis ? API.marketPromotion.misApprovals : API.marketPromotion.approvals,
        { params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE } },
      );
      return mpApprovalResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function decideDonation(
  detailId: number,
  payload: DecideDonationPayload,
): Promise<void> {
  await apiClient.patch(API.marketPromotion.decide(detailId), payload);
}

export async function recommendDonation(
  detailId: number,
  payload: Omit<DecideDonationPayload, 'approveTypeNo'>,
): Promise<void> {
  await apiClient.patch(API.marketPromotion.recommend(detailId), payload);
}

export async function amendDonation(
  detailId: number,
  payload: Omit<DecideDonationPayload, 'approveTypeNo'>,
): Promise<void> {
  await apiClient.patch(API.marketPromotion.amend(detailId), payload);
}

export async function verifyMis(detailId: number, payload: VerifyMisPayload): Promise<void> {
  await apiClient.patch(API.marketPromotion.verifyMis(detailId), payload);
}

const ISO_DATE_LENGTH = 10;
const stamp = (): string => new Date().toISOString().slice(0, ISO_DATE_LENGTH);

export async function exportMpApprovals(params: MpApprovalSearch): Promise<void> {
  const res = await apiClient.get(API.marketPromotion.approvalsExport, {
    params,
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `market-promotion-approval-${stamp()}.xlsx`);
}
